import * as React from 'react';
import * as ReactDOM from 'react-dom';
import ReactResizeDetector from 'react-resize-detector';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';
import { ISizeWithTopOffset } from '../../models/ISizeWithTopOffset';
import { SearchResultItem } from '../../models/SearchResultTypes';
import { ContentType } from '../../models/ContentType';
import { classNames } from '../../../../homepage/Utils';
import * as Layout from '../../../common/styles/Layout';

/**
 * Amount of extra items to fetch/render above and below the visible area, expressed as a fraction
 * of the height of the visible area
 */
export const SECTION_VERTICAL_BUFFER = 0.7;

export interface CardScrollerProps {
  /**
   * A sparse array of the items to show in the scroll view.
   * Fetched items should live at their respective
   * indexes, i.e. this array represents the full list of items, not just a particular slice.
   */
  readonly items?: SearchResultItem[];

  /**
   * Is the number of items since the items are a sparse array
   */
  readonly itemsTotal: number;

  /**
   * A function that requests items from the start to the end index, inclusive.
   * It is expected that, some time later, the missing items will be passed back in via the "items" prop.
   */
  readonly requestContentSlice: (start: number, end: number, contentType: ContentType) => void;

  /** Component for the fetched card */
  readonly renderFetchedCard: (props: { item: SearchResultItem; index: number; row: number }) => JSX.Element;

  /** Component for the unfetched card */
  readonly renderUnfetchedCard: () => JSX.Element;

  /** Each fetched item card must have a unique key; this function should provide those keys */
  readonly getKeyForItem: (item: SearchResultItem) => string;

  /** The height of each card. This is used to calculate the paging. */
  readonly itemHeightInPx: number;

  /** The width of each card. This is used to calculate the paging. */
  readonly itemWidthInPx: number;

  /**
   * The minimum number of pixels between cards. This number is static in the vertical dimension,
   * but may be larger in the horizontal dimension.
   */
  readonly itemSpacingInPx: number;

  /**
   * The maximum width of the entire card section. If the available space is wider than the max width,
   * the card scroller will be the max width and centered within the available space.
   */
  readonly cardScrollerMaxWidth?: number;

  /**
   * The horizontal padding on the items container, on either the left or the right (they are
   * assumed to be equal). This is used to calculate the paging.
   */
  readonly sectionHorizontalPaddingInPx: number;

  /**
   * Callback that gets invoked after the asynchronous layout update is complete.
   */
  readonly onLayoutUpdated?: () => void;

  readonly contentType: ContentType;

  /**
   * Detects if single content type page has loaded meta data and is ready to fetch data for scrolling
   */
  readonly initialDataFullyLoaded: boolean | undefined;
}

export interface CardScrollerState {
  /** The measured size of the active section */
  readonly measuredSectionDimensions: ISizeWithTopOffset | undefined;
}

//This has to be an impure component as we want this to update everytime the cardview state/props changes
export class CardScroller extends React.Component<CardScrollerProps, CardScrollerState> {
  private element: Element | undefined;

  private memoizedGetCardDimensionsStyle = defaultMemoize(CardScrollerHelpers.getCardDimensionsStyle);

  private constructor(props: CardScrollerProps) {
    super(props);

    this.state = {
      measuredSectionDimensions: undefined,
    };
  }

  public componentDidMount() {
    // eslint-disable-next-line react/no-find-dom-node
    this.element = ReactDOM.findDOMNode(this) as Element;
    this.updateLayoutAsynchronously();
  }

  public componentWillUnmount() {
    this.element = undefined;
  }

  public componentDidUpdate(prevProps: CardScrollerProps, prevState: CardScrollerState) {
    const itemCountHasChanged = prevProps.itemsTotal !== this.props.itemsTotal;
    if (this.element && itemCountHasChanged) {
      this.element.scrollTop = 0;
    }

    if (this.state.measuredSectionDimensions) {
      const { cardRange } = CardScrollerHelpers.getCardAndRowRangeForSection({
        sectionDimensions: this.state.measuredSectionDimensions,
        itemHeightInPx: this.props.itemHeightInPx,
        itemWidthInPx: this.props.itemWidthInPx,
        itemSpacingInPx: this.props.itemSpacingInPx,
      });
      const count = cardRange.end - cardRange.start;

      if (this.state.measuredSectionDimensions.topOffset !== prevState.measuredSectionDimensions?.topOffset) {
        // the saga takes care of snapping to whole page increments and deduping the same requests
        if (this.props.initialDataFullyLoaded) {
          this.props.requestContentSlice(cardRange.start, count, this.props.contentType);
        }
      }
      if (this.state.measuredSectionDimensions !== prevState.measuredSectionDimensions) {
        this.props.onLayoutUpdated && this.props.onLayoutUpdated();
      }
    }
  }

  private handleScroll = () => {
    this.updateLayoutAsynchronously();
  };

  private handleResize = () => {
    this.updateLayoutAsynchronously();
  };

  // We must use defer because the element needs to have rendered on the screen before we measure it
  private updateLayoutAsynchronously = () =>
    _.defer(() => {
      if (this.element) {
        const measuredSectionDimensions = CardScrollerHelpers.measureSectionDimensions(
          this.element,
          this.props.sectionHorizontalPaddingInPx,
          SECTION_VERTICAL_BUFFER,
          this.props.cardScrollerMaxWidth
        );
        this.setState({ measuredSectionDimensions });
      }
    });

  public render() {
    let totalHeightDivStyle: React.CSSProperties;
    let sectionStyle: React.CSSProperties;
    let cardsInSection: JSX.Element[];

    const totalItemCount = this.props.items?.length || 0;

    if (this.state.measuredSectionDimensions) {
      const cardWidthWithSpacing = this.props.itemWidthInPx + this.props.itemSpacingInPx;
      const numberOfColumns = CardScrollerHelpers.calculateNumberOfColumns(
        this.state.measuredSectionDimensions.width,
        cardWidthWithSpacing
      );
      const totalRowCount = Math.ceil(totalItemCount / numberOfColumns);

      const itemHeightWithSpacing = this.props.itemHeightInPx + this.props.itemSpacingInPx;
      const sectionMarginBottom = this.props.itemSpacingInPx;
      const totalHeight = totalRowCount ? totalRowCount * itemHeightWithSpacing + sectionMarginBottom : 0;

      totalHeightDivStyle = {
        height: totalHeight,
        maxWidth: this.props.cardScrollerMaxWidth,
      };

      const { cardRange, rowRange } = CardScrollerHelpers.getCardAndRowRangeForSection({
        sectionDimensions: this.state.measuredSectionDimensions,
        itemHeightInPx: this.props.itemHeightInPx,
        itemWidthInPx: this.props.itemWidthInPx,
        itemSpacingInPx: this.props.itemSpacingInPx,
      });
      const sectionTopOffsetSnappedToRow = rowRange.start * itemHeightWithSpacing;

      sectionStyle = {
        position: 'absolute',
        top: Math.max(sectionTopOffsetSnappedToRow, 0),
        left: 0,
        right: 0,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: totalItemCount <= numberOfColumns ? 'flex-start' : 'space-around',
        paddingLeft: this.props.sectionHorizontalPaddingInPx,
        paddingRight: this.props.sectionHorizontalPaddingInPx,
        marginBottom: sectionMarginBottom,
      };

      cardsInSection = this.props.items ? this.makeCardElementsForSection(this.props.items, cardRange, numberOfColumns) : [];

      /* eslint-disable jsx-a11y/no-static-element-interactions */
      return (
        <div
          className={classNames(Layout.absolute, Layout.fulfill, Layout.overflowAuto)}
          data-tb-test-id={CardScrollerTestIds.CardView}
          data-tb-total-item-count={totalItemCount}
          onScroll={_.debounce(() => this.handleScroll(), 300)}
        >
          <div className={Layout.relative} style={totalHeightDivStyle}>
            <section style={sectionStyle} data-tb-test-id={CardScrollerTestIds.ActiveSection}>
              {cardsInSection}
            </section>
          </div>
          <ReactResizeDetector handleWidth handleHeight onResize={this.handleResize} />
        </div>
      );
    } else {
      return <div></div>;
    }
  }

  private makeCardElementsForSection(
    items: ReadonlyArray<SearchResultItem>,
    cardRange: { start: number; end: number },
    numberOfColumns: number
  ): JSX.Element[] {
    const totalItemCount = items.length;
    const fillerCardCount = CardScrollerHelpers.calculateFillerCardsNeeded({
      actualItemCount: totalItemCount,
      cardsPerRow: numberOfColumns,
    });

    // If section ends beyond the end of the item list, cut it off
    const boundedEndIndex = Math.min(cardRange.end, totalItemCount + fillerCardCount - 1);

    const cards: JSX.Element[] = [];
    for (let i = cardRange.start; i <= boundedEndIndex; i++) {
      cards.push(this.getCardAtIndex(i, items, numberOfColumns));
    }

    return cards;
  }

  private getCardAtIndex(index: number, items: ReadonlyArray<SearchResultItem>, numberOfColumns: number) {
    const cardDimensionsStyle = this.memoizedGetCardDimensionsStyle(
      this.props.itemHeightInPx,
      this.props.itemWidthInPx,
      this.props.itemSpacingInPx
    );

    const FetchedCardComponent = this.props.renderFetchedCard;
    const UnfetchedCardComponent = this.props.renderUnfetchedCard;

    const isFillerCard = index >= items.length;
    const isFetchedCard = !!items[index];

    if (isFillerCard) {
      return <div style={cardDimensionsStyle} key={`filler-${index}`} />;
    } else if (isFetchedCard) {
      const item = items[index];

      return (
        <div style={cardDimensionsStyle} key={`${index}-${this.props.getKeyForItem(item)}`}>
          <FetchedCardComponent item={item} index={index} row={Math.floor(index / numberOfColumns)} />
        </div>
      );
    } else {
      return (
        <div style={cardDimensionsStyle} key={`unfetched-${index}`}>
          <UnfetchedCardComponent />
        </div>
      );
    }
  }
}

export namespace CardScrollerTestIds {
  export const CardView = 'tb-card-view';
  export const ActiveSection = 'tb-card-view-active-section';
}

export namespace CardScrollerHelpers {
  export function getCardDimensionsStyle(height: number, width: number, spacing: number): React.CSSProperties {
    return {
      height: height,
      width: width,
      marginTop: spacing,
      marginRight: spacing / 2,
      marginBottom: 22,
      marginLeft: spacing / 2,
    };
  }

  /**
   * Returns the measured size, in pixels, of the section to render. Does not trim any boundaries
   * (i.e. the top offset could be negative).
   */
  export function measureSectionDimensions(
    element: Element,
    sectionHorizontalPadding: number,
    sectionVerticalBuffer: number,
    cardScrollerMaxWidth = Infinity
  ): ISizeWithTopOffset {
    // Because we don't allow horizontal overflow/scrolling, we can safely use scrollWidth, instead
    // of clientWidth. We must use clientHeight, however, because we do allow vertical scrolling
    // (we want only the height of what the user sees).
    const widthThatCanDisplayCards = Math.min(element.scrollWidth, cardScrollerMaxWidth || Infinity);
    const widthMinusPadding = widthThatCanDisplayCards - 2 * sectionHorizontalPadding;

    const visibleHeight = element.clientHeight;
    const bufferHeight = visibleHeight * sectionVerticalBuffer;
    const sectionHeight = visibleHeight + 1.5 * bufferHeight;

    const topOffset = element.scrollTop - bufferHeight;

    return {
      height: sectionHeight,
      width: widthMinusPadding,
      topOffset: topOffset,
    };
  }

  export function calculateNumberOfColumns(sectionWidth: number, columnWidth: number): number {
    return Math.floor(Math.max(sectionWidth / columnWidth, 1));
  }

  export function calculateFillerCardsNeeded(params: { actualItemCount: number; cardsPerRow: number }): number {
    const { actualItemCount, cardsPerRow } = params;
    const trailingItemCount = cardsPerRow > 0 ? actualItemCount % cardsPerRow : 0;
    return trailingItemCount > 0 ? cardsPerRow - trailingItemCount : 0;
  }

  export interface SectionRanges {
    cardRange: {
      start: number;
      end: number;
    };
    rowRange: {
      start: number;
      end: number;
    };
  }

  export interface GetCardAndRowRangeParams {
    sectionDimensions: ISizeWithTopOffset;
    itemHeightInPx: number;
    itemWidthInPx: number;
    itemSpacingInPx: number;
  }

  /**
   * Returns the indexes of the first and last card/row that should be in the section (the visible portion
   * of the list, plus a buffer above and below). If the section is at the beginning of the list where
   * the top buffer would go into negative indexes, they will get cut off at 0.
   */
  export function getCardAndRowRangeForSection(params: GetCardAndRowRangeParams): SectionRanges {
    const { sectionDimensions, itemHeightInPx, itemWidthInPx, itemSpacingInPx } = params;

    const cardHeight = itemHeightInPx + itemSpacingInPx;
    const cardWidth = itemWidthInPx + itemSpacingInPx;

    const numberOfColumns = calculateNumberOfColumns(sectionDimensions.width, cardWidth);

    const indexOfFirstRowInSection = Math.floor(sectionDimensions.topOffset / cardHeight);
    const indexOfFirstCardInSection = indexOfFirstRowInSection * numberOfColumns;

    const indexOfFirstRowPastEndOfSection = Math.ceil((sectionDimensions.topOffset + sectionDimensions.height) / cardHeight);
    const indexOfLastCardInSection = indexOfFirstRowPastEndOfSection * numberOfColumns - 1;

    return {
      cardRange: {
        start: Math.max(indexOfFirstCardInSection, 0),
        end: indexOfLastCardInSection,
      },
      rowRange: {
        start: Math.max(indexOfFirstRowInSection, 0),
        end: indexOfFirstRowPastEndOfSection - 1,
      },
    };
  }
}
